import { Align, Popup as KPopup, Offset } from '@progress/kendo-react-popup';
import { FC, PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';

interface PopUpProps {
  children: React.ReactNode;
  content: React.ReactNode;
  anchorAlign?: Align;
  popupAlign?: Align;
  offset?: Offset;
  className?: string;
  show?: boolean;
  style?: React.CSSProperties | undefined;
}
const PopUp: FC<PopUpProps> = ({
  children,
  content,
  anchorAlign = { horizontal: 'right', vertical: 'bottom' },
  popupAlign = { horizontal: 'right', vertical: 'top' },
  offset,
  className = '',
  show: showPopUp = true,
  style,
}) => {
  const [show, setShow] = useState(false);
  const anchor = useRef<HTMLDivElement | null>(null);
  const popupContent = useRef<HTMLDivElement | null>(null);
  // Touch has no hover: a tap opens the popup and it stays open until the next tap outside.
  // While touch-opened, the browser's emulated mouseleave events must not close it.
  const openedByTouch = useRef(false);

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse') {
      return;
    }
    openedByTouch.current = true;
    setShow(true);
  };

  const handleMouseLeave = () => {
    if (openedByTouch.current) {
      return;
    }
    setShow(false);
  };

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (!openedByTouch.current) {
        return;
      }
      const target = event.target as Node;
      if (anchor.current?.contains(target) || popupContent.current?.contains(target)) {
        return;
      }
      openedByTouch.current = false;
      setShow(false);
    };

    document.addEventListener('pointerdown', handleOutsidePointerDown, true);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
  }, [show]);

  return (
    <div
      onMouseEnter={() => setShow(true)}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
      ref={anchor}
    >
      {children}
      {show && content ? (
        <KPopup
          style={style}
          anchor={offset ? null : anchor.current}
          anchorAlign={anchorAlign}
          popupAlign={popupAlign}
          show={show && showPopUp}
          animate={false}
          offset={offset}
          className={className}
        >
          <div ref={popupContent}>{content}</div>
        </KPopup>
      ) : undefined}
    </div>
  );
};

export default PopUp;
